// components/GlobalStyles/GlobalStyles.tsx
import { C_BASE, LIGHT_THEME, DARK_THEME, FONT_HEAD, FONT_BODY } from "../../constants/theme";

export default function GlobalStyles() {
  return (
    <style>{`
      /* Plus Jakarta Sans now loaded via <link> in public/index.html
         instead of this JS-rendered @import — same font, discovered
         at first paint instead of after mount. */

      /* Light theme (default) */
      :root {
        --color-orange: ${C_BASE.orange};
        --color-orange-alt: ${C_BASE.orangeAlt};
        --color-orange-light: ${C_BASE.orangeLight};
        --color-peach: ${C_BASE.peach};
        --color-gold: ${C_BASE.gold};
        --color-green: ${C_BASE.green};
        
        --color-text-dark: ${LIGHT_THEME.darkText};
        --color-text-mid: ${LIGHT_THEME.midText};
        --color-text-muted: ${LIGHT_THEME.mutedText};
        --color-white: ${LIGHT_THEME.white};
        --color-bg: ${LIGHT_THEME.bg};
        --color-cream: ${LIGHT_THEME.cream};
        --color-border: ${LIGHT_THEME.border};
        --color-card-bg: ${LIGHT_THEME.cardBg};
        --color-overlay-card: ${LIGHT_THEME.overlayCard};
        --color-glass-bg: ${LIGHT_THEME.glassBg};
        
        --bg-gradient: ${LIGHT_THEME.bgGradient};
        --hero-gradient: ${LIGHT_THEME.heroGradient};
        
        --font-head: ${FONT_HEAD};
        --font-body: ${FONT_BODY};
        
        --radius-sm: 8px;
        --radius-md: 14px;
        --radius-lg: 22px;
        --radius-xl: 30px;
        --radius-full: 9999px;
        
        --shadow-card: 0 2px 12px rgba(0,0,0,0.06),0 1px 4px rgba(0,0,0,0.04);
        --shadow-hover: 0 12px 36px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
        --shadow-orange: 0 8px 24px rgba(232,65,10,0.28);
      }

      /* Dark theme */
      .dark {
        --color-text-dark: ${DARK_THEME.darkText};
        --color-text-mid: ${DARK_THEME.midText};
        --color-text-muted: ${DARK_THEME.mutedText};
        --color-white: ${DARK_THEME.white};
        --color-bg: ${DARK_THEME.bg};
        --color-cream: ${DARK_THEME.cream};
        --color-border: ${DARK_THEME.border};
        --color-card-bg: ${DARK_THEME.cardBg};
        --color-overlay-card: ${DARK_THEME.overlayCard};
        --color-glass-bg: ${DARK_THEME.glassBg};
        
        --bg-gradient: ${DARK_THEME.bgGradient};
        --hero-gradient: ${DARK_THEME.heroGradient};
        
        --shadow-card: 0 2px 12px rgba(0,0,0,0.3),0 1px 4px rgba(0,0,0,0.2);
        --shadow-hover: 0 12px 36px rgba(0,0,0,0.4),0 2px 8px rgba(0,0,0,0.2);
      }

      *,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
      html { scroll-behavior:smooth; -webkit-text-size-adjust:100%; }
      body {
        font-family: "Plus Jakarta Sans", Nunito, sans-serif;
        font-size: clamp(0.875rem,1vw,1rem);
        color: var(--color-text-dark);
        background: var(--color-bg);
        line-height: 1.6;
        margin: 0; padding: 0; 
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        transition: background-color 0.3s ease, color 0.3s ease;
      }
      
      img,video { display:block; max-width:100%; }
      a { color:inherit; text-decoration:none; }
      button { font-family:"Plus Jakarta Sans", Nunito, sans-serif; }

      /* Keyframes remain the same */
      @keyframes slideInLeft  { from{opacity:0;transform:translateX(-28px)} to{opacity:1;transform:translateX(0)} }
      @keyframes fadeUp       { from{opacity:0;transform:translateY(18px)}  to{opacity:1;transform:translateY(0)} }
      @keyframes fadeIn       { from{opacity:0} to{opacity:1} }
      @keyframes scaleIn      { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }
      @keyframes pulse-dot    { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:0.7} }
      @keyframes marquee      { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
      @keyframes carouselFade { 0%{opacity:0;transform:translateY(16px)} 10%{opacity:1;transform:translateY(0)} 88%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(-12px)} }
      @keyframes cardSlideIn  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      @keyframes shimmer      { 0%{background-position:-200% center} 100%{background-position:200% center} }
      @keyframes spin         { to{transform:rotate(360deg)} }

      .carousel-slide { animation: carouselFade 5s ease-in-out forwards; }

      /* Nav styles using CSS variables */
      .nav-link {
        font-family: "Plus Jakarta Sans", Nunito, sans-serif;
        font-size: clamp(0.8rem,0.9vw,0.875rem);
        font-weight: 600;
        color: var(--color-text-dark);
        text-decoration: none;
        padding: 6px 14px;
        border-radius: var(--radius-full);
        transition: background 0.2s, color 0.2s;
        white-space: nowrap;
      }

      .scroll-toggle {
        position: fixed;
        bottom: 24px;
        right: 24px;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: none;
        background: #DC2626;
        color: white;
        font-size: 22px;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(0,0,0,0.2);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      }

      .scroll-toggle:hover {
        transform: scale(1.1);
      }
      
      .nav-link.active { color: var(--color-orange); font-weight:700; }

      /* Buttons using CSS variables */
      .btn-primary {
        display:inline-flex; align-items:center; justify-content:center; gap:6px;
        background: var(--color-orange); color:#fff; border:none;
        padding: clamp(9px,1vw,11px) clamp(18px,2vw,26px);
        border-radius: var(--radius-full);
        font-family: "Plus Jakarta Sans", Nunito, sans-serif; font-weight:700;
        font-size: clamp(0.8rem,0.9vw,0.9rem);
        cursor:pointer; transition:transform 0.2s,box-shadow 0.2s,background 0.2s;
        white-space:nowrap; letter-spacing:0.01em;
      }
      .btn-primary:hover { background: var(--color-orange-alt); transform:translateY(-2px); box-shadow:var(--shadow-orange); }
      .btn-primary:active { transform:translateY(0); }

      .btn-ghost {
        display:inline-flex; align-items:center; justify-content:center;
        background:transparent; color:var(--color-text-dark); border:none;
        padding: clamp(9px,1vw,11px) clamp(14px,1.5vw,20px);
        border-radius:var(--radius-full); font-family:"Plus Jakarta Sans", Nunito, sans-serif; font-weight:600;
        font-size:clamp(0.8rem,0.9vw,0.9rem); cursor:pointer; transition:color 0.2s; white-space:nowrap;
      }
      .btn-ghost:hover { color:var(--color-orange); }

      .btn-outline {
        display:inline-flex; align-items:center; justify-content:center;
        background:transparent; color:var(--color-orange);
        border:2px solid var(--color-orange);
        padding:clamp(9px,1vw,10px) clamp(18px,2vw,26px);
        border-radius:var(--radius-full); font-family:"Plus Jakarta Sans", Nunito, sans-serif; font-weight:700;
        font-size:clamp(0.8rem,0.9vw,0.9rem); cursor:pointer; transition:background 0.2s,color 0.2s; white-space:nowrap;
      }
      .btn-outline:hover { background:var(--color-orange); color:#fff; }

      /* Pills / labels */
      .section-pill {
        display:inline-block; background:rgba(232,65,10,0.09); color:var(--color-orange);
        font-family:"Plus Jakarta Sans", Nunito, sans-serif; font-weight:700;
        font-size:clamp(0.62rem,0.75vw,0.72rem); letter-spacing:0.1em; text-transform:uppercase;
        padding:4px 14px; border-radius:var(--radius-full);
      }

      .stat-divider { width:1px; background:var(--color-border); height:40px; }

      /* Marquee */
      .marquee-track {
        display:flex; gap:clamp(40px,5vw,72px);
        animation:marquee 30s linear infinite;
        width:max-content; align-items:center;
      }
      .marquee-track:hover { animation-play-state:paused; }

      /* Hamburger */
      .hamburger { display:none; flex-direction:column; gap:5px; cursor:pointer; padding:4px; background:none; border:none; }
      .hamburger span { display:block; width:24px; height:2px; background:var(--color-text-dark); border-radius:2px; transition:all 0.3s; }

      /* Subject badges */
      .badge-maths       { background:rgba(37,99,235,0.10);   color: ${C_BASE.subject.maths}; }
      .badge-physics     { background:rgba(124,58,237,0.10);  color: ${C_BASE.subject.physics}; }
      .badge-chemistry   { background:rgba(5,150,105,0.10);   color: ${C_BASE.subject.chemistry}; }
      .badge-biology     { background:rgba(220,38,38,0.10);   color: ${C_BASE.subject.biology}; }
      .badge-english     { background:rgba(180,83,9,0.10);    color: ${C_BASE.subject.english}; }
      .badge-superpowers { background:rgba(245,158,11,0.10);  color: ${C_BASE.subject.superpowers}; }
      .badge-sst         { background:rgba(8,145,178,0.10);   color: ${C_BASE.subject.sst}; }
      .badge-hindi       { background:rgba(107,114,128,0.10); color: ${C_BASE.subject.hindi}; }

      /* Responsive breakpoints */
      @media (min-width: 2560px) {
        body { font-size: 1.1rem; }
        .nav-link { font-size: 1rem; }
        .btn-primary, .btn-ghost { font-size: 1rem; padding: 13px 32px; }
      }

      @media (max-width: 1024px) {
        .nav-links-desktop { display:none !important; }
        .hamburger         { display:flex !important; }
        .banner-stage      { gap: 32px !important; }
        .pricing-grid      { flex-direction:column; align-items:center; gap:20px; }
        .pricing-card-left, .pricing-card-center, .pricing-card-right {
          flex:none; width:100%; max-width:520px; margin-left:0; margin-right:0;
        }
      }

      @media (max-width: 900px) {
        .banner-stage     { flex-direction:column !important; gap:0 !important; }
        .banner-card-col  {
          position:relative !important; width:100% !important;
          margin-right:0 !important; margin-bottom:-20px !important;
          padding-left:16px !important; padding-right:16px !important; z-index:10;
        }
        .banner-video-col { width:100% !important; }
        .lessons-grid     { grid-template-columns:repeat(2,1fr) !important; }
        .features-grid    { grid-template-columns:repeat(2,1fr) !important; }
        .directors-stack  { padding:0 16px !important; }
      }

      @media (max-width: 768px) {
        .banner-inner       { flex-direction:column !important; gap:24px !important; }
        .banner-video-wrap  { width:100% !important; min-height:200px !important; }
        .banner-content-wrap{ width:100% !important; }
        .stats-bar          { flex-wrap:wrap; gap:16px !important; }
        .stats-bar .stat-divider { display:none; }
        .stats-bar-grid     { grid-template-columns:repeat(2,1fr) !important; }
        .testimonials-grid  { grid-template-columns:1fr !important; }
        .lessons-grid       { grid-template-columns:repeat(2,1fr) !important; }
      }

      @media (max-width: 640px) {
        .features-grid     { grid-template-columns:1fr !important; }
        .stats-bottom-grid { grid-template-columns:1fr 1fr !important; }
        .lessons-grid      { grid-template-columns:1fr !important; }
        .pricing-grid      { flex-direction:column !important; align-items:center !important; }
        .testimonials-grid { grid-template-columns:1fr !important; }
        .stats-bar-grid    { grid-template-columns:repeat(2,1fr) !important; }
        .stats-bar-cell:nth-child(2), .stats-bar-cell:nth-child(4) { border-right:none !important; }
        .stats-bar-cell:nth-child(1), .stats-bar-cell:nth-child(2) { border-bottom:1px solid var(--color-border) !important; }
        .banner-outer-wrap { padding:20px 12px 0 !important; }
        .footer-inner      { height:auto !important; padding:20px 0 !important; flex-direction:column !important; align-items:center !important; gap:14px !important; }
      }

      @media (max-width: 480px) {
        .features-grid   { grid-template-columns:1fr !important; }
        .lessons-grid    { grid-template-columns:1fr !important; }
        .nav-links-desktop { display:none !important; }
        .hamburger       { display:flex !important; }
        .message-card-content { flex-direction:column !important; align-items:flex-start !important; gap:16px !important; }
      }

      @media (max-width: 360px) {
        .btn-primary { padding:9px 16px; font-size:0.78rem; }
        .btn-ghost   { padding:9px 12px; font-size:0.78rem; }
      }
    `}</style>
  );
}
